import React, { createContext, useState, useContext, useCallback, useEffect } from 'react';
import { supabase } from '../supabaseClient';
import { useSecteur } from './SecteurContext'; // Ajouter cet import

const MandateContext = createContext();

// Déplacer les fonctions utilitaires en dehors du composant
const isDateRangeActive = (startDate, endDate) => {
  const today = new Date();
  const start = startDate ? new Date(startDate) : null;
  const end = endDate ? new Date(endDate) : null;

  return (
    // Cas 1 : Date de début définie, date de fin non définie
    (start && !end && start <= today) ||
    
    // Cas 2 : Date de début non définie, date de fin définie
    (!start && end && today <= end) ||
    
    // Cas 3 : Ni date de début, ni date de fin définies
    (!start && !end) ||
    
    // Cas 4 : Les deux dates définies et aujourd'hui entre les deux
    (start && end && start <= today && today <= end)
  );
};

const transformMandateData = (mandates) => {
  if (!mandates) return [];

  return mandates
    .map(mandate => {
      // Si pas de code de relève, créer une entrée avec les informations de base
      if (!mandate.releve_codes) {
        return {
          id: mandate.id,
          fullId: mandate.id,
          fullMandateId: mandate.id.split('-')[0],
          releveCodeId: null,
          name: mandate.users?.full_name || 'N/A',
          mandate: 'N/A',
          mandateDescription: 'N/A',
          startDate: mandate.start_date,
          endDate: mandate.end_date,
          sector: mandate.sectors?.name || 'N/A',
          sector_is_active: mandate.sectors?.is_active || false,
          hoursPerPeriod: 'N/A',
          periodicity: 'N/A',
          releveCode: 'N/A',
          role: mandate.releve_codes?.role || '',  // Ajouter le role
          isActive: mandate.is_active ?? isDateRangeActive(mandate.start_date, mandate.end_date)
        };
      }

      return {
        id: mandate.id,
        fullId: mandate.id,
        fullMandateId: mandate.id.split('-')[0],
        releveCodeId: mandate.releve_codes.id,
        name: mandate.users?.full_name || 'N/A',
        mandate: mandate.releve_codes.role || 'N/A', 
        mandateDescription: 'N/A', 
        startDate: mandate.start_date,
        endDate: mandate.end_date,
        sector: mandate.sectors?.name || 'N/A',
        sector_is_active: mandate.sectors?.is_active || false,
        hoursPerPeriod: mandate.releve_codes.hours_per_month || 'N/A',
        periodicity: 'Mensuel', 
        releveCode: mandate.releve_codes.code || 'N/A',
        role: mandate.releve_codes.role || '',  // Ajouter le role
        isActive: mandate.is_active ?? isDateRangeActive(mandate.start_date, mandate.end_date)
      };
    })
    .filter(mandate => mandate.isActive);  // Ne garder que les mandats actifs
};

const calculateStats = (mandatesData) => {
  const activeCount = mandatesData.filter(m => isDateRangeActive(m.start_date, m.end_date)).length;
  
  const totalReliefHours = mandatesData.reduce((acc, mandate) => {
    // Si le mandat n'est pas actif ou n'a pas de code de relève, on ne compte pas les heures
    if (!isDateRangeActive(mandate.start_date, mandate.end_date) || !mandate.releve_codes) {
      return acc;
    }

    const hoursPerMonth = mandate.releve_codes.hours_per_month || 0;

    // Pour les mandats mensuels, on multiplie par 12
    return acc + (hoursPerMonth * 12);
  }, 0);

  return {
    activeMandates: activeCount,
    availableRelief: totalReliefHours,
    usedRelief: 0, 
    totalRelief: totalReliefHours
  };
};

export function MandateProvider({ children }) {
  const [mandates, setMandates] = useState([]);
  const [stats, setStats] = useState({
    activeMandates: 0,
    availableRelief: 0,
    usedRelief: 0,
    totalRelief: 0
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { selectedSecteur } = useSecteur(); // Ajouter cette ligne

  const fetchMandates = useCallback(async () => {
    try {
      setLoading(true);
      
      // Ne charger les mandats que si un secteur est sélectionné
      if (!selectedSecteur) {
        setMandates([]);
        setStats({
          activeMandates: 0,
          availableRelief: 0,
          usedRelief: 0,
          totalRelief: 0
        });
        return;
      }
      
      const { data, error } = await supabase
        .from('user_mandates')
        .select(`
          id,
          is_active,
          start_date,
          end_date,
          users (full_name, id),
          sectors (id, name, is_active),
          releve_code_id,
          releve_codes (
            id, 
            code, 
            hours_per_month,
            role
          )
        `)
        .eq('sector_id', selectedSecteur) // Ajouter ce filtre
        .order('start_date', { ascending: false });

      if (error) throw error;

      const transformedData = transformMandateData(data);
      setMandates(transformedData);
      
      const mandateStats = calculateStats(data);
      setStats(mandateStats);

    } catch (error) {
      console.error('Erreur lors de la récupération des mandats:', error);
      setError(error);
    } finally {
      setLoading(false);
    }
  }, [selectedSecteur, setMandates, setStats, setLoading, setError]);

  const refreshMandates = useCallback(() => {
    fetchMandates();
  }, [fetchMandates]);

  // Charger les mandats quand le secteur change
  useEffect(() => {
    if (selectedSecteur) {
      fetchMandates();
    }
  }, [selectedSecteur, fetchMandates]);

  const value = {
    mandates,
    stats,
    loading,
    error,
    refreshMandates
  };

  return (
    <MandateContext.Provider value={value}>
      {children}
    </MandateContext.Provider>
  );
}

export function useMandates() {
  const context = useContext(MandateContext);
  if (context === undefined) {
    throw new Error('useMandates must be used within a MandateProvider');
  }
  return context;
}
